import { ChatRequestError, toChatError } from './errors';

export interface MintedToken { token: string; wsUrl: string; expiresAt: number }
export interface PublicSession { id: string; mode: string; status: string; help_requested?: boolean; created_at?: string }
export interface SnapshotAgent { agent_uuid?: string; name?: string; features?: string[]; welcome?: unknown }
export interface Snapshot {
  agent?: SnapshotAgent;
  session: PublicSession | null;
  messages: unknown[];
  active_interaction?: unknown;
  interaction_cursor?: string;
  interaction_response_message?: unknown;
}
export interface Bootstrap extends Snapshot { agent: { agent_uuid: string; name: string; features: string[]; welcome?: unknown } }

const DEFAULT_TTL_MS = 300_000;

export function normalizeExpiry(expiresAt: number | undefined, now: number = Date.now()): number {
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return now + DEFAULT_TTL_MS;
  return expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
}

export class HttpClient {
  private readonly base: string;

  constructor(baseUrl: string, private readonly fetchImpl: typeof fetch = (...a) => globalThis.fetch.bind(globalThis)(...a)) {
    this.base = baseUrl.replace(/\/+$/, '');
  }

  resolveUrl(path: string): string {
    return `${this.base}${path}`;
  }

  /** Without a customer id this is the page-load probe: it never reads or creates a visitor. */
  bootstrap(agentId: string, customerId?: string, signal?: AbortSignal): Promise<Bootstrap> {
    const q = customerId === undefined ? '' : `?customer_id=${encodeURIComponent(customerId)}`;
    return this.request<Bootstrap>(`/v1/public/chat/agents/${encodeURIComponent(agentId)}/bootstrap${q}`, { signal });
  }

  createSession(agentId: string, customerId: string): Promise<PublicSession> {
    return this.request<PublicSession>(`/v1/public/chat/agents/${encodeURIComponent(agentId)}/session`, {
      method: 'POST', body: JSON.stringify({ customer_id: customerId }),
    });
  }

  async mintPublicToken(sessionId: string): Promise<MintedToken> {
    const d = await this.request<{ token?: string; ws_url?: string; expires_at?: number }>(
      `/v1/public/chat/session/${encodeURIComponent(sessionId)}/ws_token`, { method: 'POST', body: '{}' });
    if (!d?.token || !d?.ws_url) throw new ChatRequestError(toChatError('stream_unavailable'), 200);
    return { token: d.token, wsUrl: d.ws_url, expiresAt: normalizeExpiry(d.expires_at) };
  }

  snapshot(sessionId: string, token: string): Promise<Snapshot> {
    return this.request<Snapshot>(`/v1/public/chat/session/${encodeURIComponent(sessionId)}/snapshot`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      const f = this.fetchImpl;   // local call: a method call would bind `this` to the client
      const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
      if (init.body !== undefined) headers['Content-Type'] = 'application/json';
      response = await f(this.resolveUrl(path), { ...init, credentials: 'omit', headers });
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') throw e;
      throw new ChatRequestError(toChatError('network', 'Network error'), 0);
    }
    let body: Record<string, unknown> | null = null;
    try { body = (await response.json()) as Record<string, unknown>; } catch { body = null; }
    if (!response.ok || body?.success === false) {
      const error = toChatError(typeof body?.error === 'string' ? body.error : undefined,
        typeof body?.message === 'string' ? body.message : undefined);
      if (typeof body?.origin === 'string') error.origin = body.origin;
      if (response.status === 429) {
        error.code = 'rate_limited';
        error.retryable = true;
        const header = Number(response.headers.get('Retry-After'));
        const fromBody = Number(body?.retry_after);
        const seconds = Number.isFinite(header) && header > 0 ? header : fromBody;
        if (Number.isFinite(seconds) && seconds > 0) error.retryAfterSeconds = seconds;
      }
      throw new ChatRequestError(error, response.status);
    }
    return ((body?.data ?? body) as T);
  }
}
